// Node template object factory — creates node template objects from raw API data

import FontUtils from '@/services/canvas/fonts/Fonts';
import useI18nGlobal from '@/utils/i18n';

const { fontFamilyGetNameByUrl } = FontUtils();
const { i18n } = useI18nGlobal();
const { t } = i18n.global;

// --- Interfaces ---

type NodePropertyPair = Record<string, unknown>;

export interface NodeTemplateData {
	id: number;
	type: string;
	shape: string | null;
	width: number;
	height: number;
	rotate: number;
	border_enabled: boolean;
	border_color: string;
	border_style: string;
	border_width: number;
	bg_enabled: boolean;
	bg_color: string;
	shadow_enabled: boolean;
	shadow_color: string;
	shadow_x: number;
	shadow_y: number;
	shadow_blur: number;
	name: string;
	title_position: string;
	text_align: string;
	font_size: number;
	font_color: string;
	font_url: string;
	font_weight: string;
	font_style: string;
	line_height: number;
	media: string | null;
	media_url: string | null;
	media_cropped: boolean;
	media_size: number;
	media_opacity: number;
}

export interface NodeTemplateObject {
	id: number;
	type: string;
	shape: string;
	radius: number;
	width: number;
	height: number;
	rotate: number;
	border: boolean;
	borderColor: string;
	borderStyle: string;
	borderWidth: number;
	surface: boolean;
	bgColor: string;
	shadow: boolean;
	shadowColor: string;
	shadowX: number;
	shadowY: number;
	shadowBlur: number;
	name: string;
	title: string;
	titlePosition: string;
	textAlign: string;
	fontSize: number;
	fontColor: string;
	fontFamily: string;
	fontWeight: string;
	fontStyle: string;
	lineHeight: number;
	media: string | null;
	mediaURL: string | null;
	mediaCropEnabled: boolean;
	mediaSize: number;
	mediaOpacity: number;
	image: HTMLImageElement | null;
}

interface Font {
	name: string;
	url: string;
}

type FontsCollection = Record<string, Font> | Font[];

// --- Mapping ---

const NODE_TEMPLATE_MAPPING: Record<string, string> = {
	surface: 'bg_enabled',
	bgColor: 'bg_color',
	border: 'border_enabled',
	borderColor: 'border_color',
	borderStyle: 'border_style',
	borderWidth: 'border_width',
	fontColor: 'font_color',
	fontSize: 'font_size',
	fontWeight: 'font_weight',
	fontFamily: 'font_url',
	fontStyle: 'font_style',
	lineHeight: 'line_height',
	height: 'height',
	width: 'width',
	rotate: 'rotate',
	media: 'media',
	mediaOpacity: 'media_opacity',
	mediaURL: 'media_url',
	mediaSize: 'media_size',
	mediaCropEnabled: 'media_cropped',
	shadowBlur: 'shadow_blur',
	shadowColor: 'shadow_color',
	shadow: 'shadow_enabled',
	shadowX: 'shadow_x',
	shadowY: 'shadow_y',
	shape: 'shape',
	textAlign: 'text_align',
	textDecoration: 'text_decoration',
	titlePosition: 'title_position',
};

function mappingInverted(mapping: Record<string, string>): Record<string, string> {
	const invertedMapping: Record<string, string> = {};
	for (const [key, value] of Object.entries(mapping)) {
		invertedMapping[value] = key;
	}
	return invertedMapping;
}

// --- Exports ---

export function createNodeTemplateObject(
	templateOriginal: NodeTemplateData,
	fonts: FontsCollection,
): NodeTemplateObject {
	return {
		id: templateOriginal.id,
		type: templateOriginal.type,
		// A partial (e.g. KAIA-seeded) template may carry no shape; fall back to
		// 'circle' as the node path does (nodeObjectFactory.ts) so it still renders.
		shape: templateOriginal.shape || 'circle',
		radius: templateOriginal.width / 2,
		width: templateOriginal.width,
		height: templateOriginal.height,
		rotate: templateOriginal.rotate,
		border: templateOriginal.border_enabled,
		borderColor: templateOriginal.border_color,
		borderStyle: templateOriginal.border_style,
		borderWidth: templateOriginal.border_width,
		surface: templateOriginal.bg_enabled,
		bgColor: templateOriginal.bg_color,
		shadow: templateOriginal.shadow_enabled,
		shadowColor: templateOriginal.shadow_color,
		shadowX: templateOriginal.shadow_x,
		shadowY: templateOriginal.shadow_y,
		shadowBlur: templateOriginal.shadow_blur,
		name: templateOriginal.name,
		title: t('objectTemplate.title'),
		titlePosition: templateOriginal.title_position,
		textAlign: templateOriginal.text_align,
		fontSize: templateOriginal.font_size,
		fontColor: templateOriginal.font_color,
		fontFamily: fontFamilyGetNameByUrl(templateOriginal.font_url, fonts),
		fontWeight: templateOriginal.font_weight,
		fontStyle: templateOriginal.font_style,
		lineHeight: templateOriginal.line_height,
		media: templateOriginal.media,
		mediaURL: templateOriginal.media_url,
		mediaCropEnabled: templateOriginal.media_cropped,
		mediaSize: templateOriginal.media_size || 100,
		mediaOpacity: templateOriginal.media_opacity || 100,
		image: null,
	};
}

export function convertOriginalProperty(
	originalProperty: NodePropertyPair,
): NodePropertyPair | undefined {
	const [[key, value]] = Object.entries(originalProperty);
	const mapping = mappingInverted(NODE_TEMPLATE_MAPPING);
	if (mapping[key]) return { [mapping[key]]: value };
}

export function convertToOriginalProperty(
	templateProperty: NodePropertyPair,
): NodePropertyPair | undefined {
	const [[key, value]] = Object.entries(templateProperty);
	if (NODE_TEMPLATE_MAPPING[key]) return { [NODE_TEMPLATE_MAPPING[key]]: value };
}
